/* eslint-disable @typescript-eslint/camelcase */
import { DateTime } from "luxon";
import { isString, each, some, range, forEach } from "../../utils/lodash";
import * as as from "./symbols";
import { isEmptyOrNaN, isNullOrEmpty, isNullOrNaN } from "../../utils";
import { INVALID } from "./consts";
import { ParserState } from "./ParserState";
import { EPOCH } from "../../utils/time";
import { getMaxLoopSize } from "../../models/utilities/blockUtilities";
import { getVariableWithName } from "../../models/utilities/questionnaireUtilities";
import { round } from "../../utils/Number";
import { QuotaStatus } from "../../models/enums/QuotaStatus";
import { parse } from "../../expressions/parser";
import { EvaluationResult } from "../../expressions/EvaluationResult";
import { setVariableMentions, setLoopRow } from "../../redux/actions";
import { getMentionsCount, getMentions } from "../../redux/reducers/interview/variablesUtilities";

/**
 * Validate functions input
 */
export function validateInput(s: ParserState, op: any, a: any, b?: any) {
    let v = [];
    v.push(as.number(s, a));
    if (b) v.push(as.number(s, b));
    if (v.some((x) => x === INVALID || (isNaN(x) && isString(x)))) return INVALID;
    if (v.some(Number.isNaN)) return NaN;
    v = v.map((x) => (x === undefined ? NaN : x));
    return v[1] !== undefined ? op(v[0], v[1]) : op(v[0]);
}

function setVariablesValues(s: ParserState, values: any[], variables: any[]) {
    // Iterate all variables.
    each(variables, (variable) => {
        // Get the variable object.
        const v = as.val(s, variable);
        if (v) {
            // Set mentions for the variable.
            s.dispatch(
                setVariableMentions(v.name, (mentions) => {
                    // Clear and add all mentions.
                    mentions.clear();
                    each(values, (value) => mentions.add(value));
                    return mentions;
                }),
            );
        }
    });
}

function getValidNumber(v: any) {
    return !v || v === "" ? 0 : v;
}

function isInvalid(v: any) {
    return isNullOrNaN(v) || v === INVALID;
}

/**
 * Validate functions output
 */
export function validateOutput(v: number) {
    if (isInvalid(v)) return INVALID;
    return isFinite(v) ? v : 0;
}

/**
 * Performs a test of set membership between variables and values.
 */
export function eq(s: ParserState, variable: any, input: any) {
    variable = as.val(s, variable);
    input = as.val(s, input);
    return variable.equals(input) ? 1 : 0;
}

/**
 * Verifies that an input number or string literal corresponds to a variable or value.
 */
export function eq_algebraic(s: ParserState, a: any, b: any) {
    const f = (v1: any, v2: any) => v1 === v2;
    return algebraic(s, a, b, f);
}

/**
 * Verifies that an input number or string literal is greater than a variable or value.
 */
export function gt(s: ParserState, a: any, b: any) {
    const f = (v1: any, v2: any) => (v1 || 0) > (v2 || 0);
    return algebraic(s, a, b, f);
}

/**
 * Verifies that an input number or string literal is less than a variable or value.
 */
export function lt(s: ParserState, a: any, b: any) {
    const f = (v1: any, v2: any) => (v1 || 0) < (v2 || 0);
    return algebraic(s, a, b, f);
}

/**
 * Algebraic comparison.
 */
export function algebraic(s: ParserState, a: any, b: any, op: any) {
    a = as.number(s, a);
    if (isEmptyOrNaN(a)) a = 0;
    b = as.number(s, b);
    if (isEmptyOrNaN(b)) b = 0;
    if ([a, b].some((v) => v === INVALID || (isNaN(v) && isString(v)))) return INVALID;
    return op(a, b);
}

/**
 * Compares every mention of a variable with a value, returning 1 if the value is greater,
 * -1 if it is less than and 0 if it is equal.
 */
export function compare(s: ParserState, variable: any, value: any) {
    variable = as.val(s, variable);
    value = as.val(s, value);
    return variable.compare(value);
}

/**
 * Returns true if the variable does not have any mentions or all mentions are empty.
 */
export function empty(s: ParserState, variable: any) {
    variable = as.val(s, variable);
    const values = variable.values();
    if (values.length === 0) return true;
    return values.every(isNullOrEmpty);
}

/**
 * Returns true if any mention of the variable matches the value.
 */
export function strfind(s: ParserState, variable: any, input: any) {
    variable = as.val(s, variable);
    input = as.string(s, input);
    return variable.values().some((v: string) => v.toLowerCase().indexOf(input.toLowerCase()) > -1);
}

/**
 * Performs a vector operation on values.
 */
export function vec(s: ParserState, values: any[], op: (v: number[]) => number) {
    values = as.val(s, values);
    if (values.length < 2) return NaN;
    const numbers = values.map((v) => as.number(s, v));
    return op(numbers);
}

/**
 * Performs a boolean operation, returning v1 if true or v2 if false.
 */
export function bool(s: ParserState, ex: any, v1?: any, v2?: any) {
    ex = as.val(s, ex);
    if (v1 === undefined) v1 = true;
    if (v2 === undefined) v2 = false;
    if (ex === INVALID) return !isInvalid(v2) ? v2 : INVALID;
    if (isEmptyOrNaN(v1)) v1 = 0;
    if (isEmptyOrNaN(v2)) v2 = 0;
    const result = (ex ? v1 : v2) || 0;
    return !isInvalid(result) ? result : INVALID;
}

/**
 * Counts mentions for multiple mentions and returns the sum.
 */
export function nbr(s: ParserState, ...vars: any[]) {
    return vars
        .map(
            (v) =>
                as
                    .val(s, v)
                    .values()
                    .filter((v: string) => !isNullOrEmpty(v)).length,
        )
        .reduce((a, b) => a + b);
}

/**
 * Compares two values. Returns v1 if a<b; v2 if a=b, v3 if a>b.
 */
export function cmp(s: ParserState, a: any, b: any = 0, v1: any = -1, v2: any = 0, v3: any = 1) {
    a = as.number(s, a);
    if (isEmptyOrNaN(a)) a = 0;
    b = as.number(s, b);
    if (isEmptyOrNaN(b)) b = 0;
    v1 = as.number(s, v1);
    v2 = as.number(s, v2);
    v3 = as.number(s, v3);
    if (some([a, b], isInvalid)) return INVALID;
    if (a === b) return v2;
    return a > b ? v3 : v1;
}

/**
 * Compares two values. Returns -1 if a<0; 0 if a=0, 1 if a>0.
 */
export function sgn(s: ParserState, a: any) {
    a = as.number(s, a);
    if (isInvalid(a)) a = INVALID;
    if (a === 0) return 0;
    return a > 0 ? 1 : -1;
}

/**
 * Compares a variable with other variables, returning how many mentions are found in all other variables listed.
 */
export function cmr(s: ParserState, v1: any, ...vn: any[]) {
    v1 = as.val(s, v1);
    vn = vn.map((v) => as.val(s, v));
    return v1.values().filter((m: string) => {
        if (isNullOrEmpty(m)) return false;
        // Return true if all vn have this mention in common.
        return vn.every((v) => v.values().includes(m));
    }).length;
}

/**
 * Returns 1 if the code present in the specified mention of a variable equals the specified code.
 */
export function meq(s: ParserState, variable: any, i: any, code: any) {
    variable = as.val(s, variable);
    const mention = getMentions(s.interview.variables, variable.name)[i - 1];
    if (!mention) return 0;
    let value: any = mention.value;
    if (typeof code === "number") value = parseFloat(mention.value);
    return value === code ? 1 : 0;
}

/**
 * Compares a variable with other variables, returning how many mentions are found in all other variables listed.
 */
export function mnb(s: ParserState, variable: any, code: any) {
    variable = as.val(s, variable);
    code = as.string(s, code);
    return (
        variable.values().findIndex((m: string) => {
            // If the mention is a number, use number comparison.
            if (/^-?[0-9]+(\.[0-9]+)?$/.test(m)) return as.number(s, code) === as.number(s, m);
            return code === m;
        }) + 1
    );
}

/**
 * Returns the index of the last vn that is less than or equal to v1.
 */
export function rng(s: ParserState, v1: any, ...vn: any[]) {
    v1 = as.number(s, v1);
    vn = vn.map((v) => as.number(s, v));
    return vn.filter((v) => v <= v1).length;
}

/**
 * RNK (A, VAL1, VAL2, VAL3..)
 * Returns the number of items in list VAL1, VAL2, VAL3.. Each value is less than or equal to A
 */
export function rnk(s: ParserState, v1: any, ...vn: any[]) {
    v1 = as.number(s, v1);
    if (v1 === null) v1 = 0;
    vn = vn.map((v) => {
        const num = as.number(s, v);
        return num === null ? 0 : num;
    });
    if ([...vn, v1].some(isInvalid)) return INVALID;
    return vn.filter((v) => v <= v1).length;
}

/**
 * Returns the index of the last vn that is greater than or equal to v1.
 */
export function ran(s: ParserState, lower: any, upper: any) {
    lower = as.number(s, lower);
    upper = as.number(s, upper);
    if ([upper, lower].some((v) => v === null) || upper === lower) return INVALID;
    const result = Math.random() * (upper - lower) + lower;
    if (s.invalidateCache) s.invalidateCache();
    return validateOutput(result);
}

/**
 * MOD (A,B) = A modulo B Remainder of division of A by B.
 */
export function mod(s: ParserState, dividend: any, divisor: any) {
    const f = (v1: any, v2: any) => {
        return round(Math.trunc(v1 || 0) % Math.trunc(v2 || 0));
    };
    return validate(s, f, dividend, divisor);
}

/**
 * Square root.
 */
export function sqr(s: ParserState, v: any) {
    v = as.number(s, v);
    if (isEmptyOrNaN(v)) return 0;
    if (v === INVALID || isNaN(v)) return INVALID;
    const result = validateInput(s, Math.sqrt, v);
    return validateOutput(result);
}

/**
 * Natural logarithm.
 */
export function ln(s: ParserState, v: any) {
    const result = validate(s, Math.log2, v);
    return result !== INVALID ? result : 0;
}

/**
 * Logarithm (base 10).
 */
export function log(s: ParserState, v: any) {
    const result = validate(s, Math.log10, v);
    return result !== INVALID ? result : 0;
}

/**
 * Absolute value.
 */
export function abs(s: ParserState, v: any) {
    return validateInput(s, Math.abs, v);
}

/**
 * Integer part (Truncation).
 */
export function trc(s: ParserState, v: any) {
    return validate(s, Math.trunc, v);
}

/**
 * Exponential (e ** A)
 */
export function exp(s: ParserState, v: any) {
    return validate(s, Math.exp, v);
}

/**
 * Validate function values
 */
export function validate(s: ParserState, op: any, v1: any, v2?: any) {
    const result = validateInput(s, op, v1, v2);
    return validateOutput(result);
}

/**
 * Julian (YEAR, MONTH, DAY).
 * Returns the absolute day from January 1, 1980, allowing to compute the number of days between two dates.
 * For example: $D – JUL (1992, MONTH, DAY).
 */
export function jul(s: ParserState, year: any, month: any, day: any) {
    year = as.number(s, year);
    month = as.number(s, month);
    day = as.number(s, day);
    const date = DateTime.fromFormat(`${year}-${month}-${day}`, "yyyy-M-d");
    return date.diff(EPOCH, "days").days;
}

/**
 * Returns the year of a Julian date.
 */
export const yea = (s: ParserState, jul: any) => as.epoch(s, jul).year;

/**
 * Returns the month of a Julian date.
 */
export const mon = (s: ParserState, jul: any) => as.epoch(s, jul).month;

/**
 * Returns the day of a Julian date.
 */
export const day = (s: ParserState, jul: any) => as.epoch(s, jul).day;

/**
 * Returns the day of the week of a Julian date (Sun=1).
 */
export const wkd = (s: ParserState, jul: any) => {
    const { weekday } = as.epoch(s, jul);
    switch (weekday) {
        case 7:
            return 1;
        default:
            return weekday + 1;
    }
};

function getQuota(s: ParserState, value: any) {
    return s.interview.quotas.input.find((i) => i.stratumId === value);
}

/**
 * Returns the number of choices in a question.
 */
export function nbc(s: ParserState, variable: any) {
    variable = as.val(s, variable);
    return getVariableWithName(s.interview.questionnaire, variable.name).choiceList.length;
}

/**
 * Returns the quota for a given quota stratum. Reference to a stratum is made by its Stratum ID. No.
 */
export function qta(s: ParserState, value: any) {
    value = as.val(s, value);
    const quota = getQuota(s, value);
    if (quota) return quota.objective;
    return 0;
}

/**
 * 	Returns the current frequency for a given stratum (quota cell).
 */
export function qtf(s: ParserState, value: any) {
    value = as.val(s, value);
    const quota = getQuota(s, value);
    if (quota) return quota.frequency;
    return 0;
}

/**
 * 	Returns the number of current interviews for a given stratum (quota cell).
 */
export function qtc(s: ParserState, value: any) {
    value = as.val(s, value);
    const quota = getQuota(s, value);
    if (quota) return quota.online;
    return 0;
}

function isStratumAvailable(s: ParserState, useOnlines: boolean, stratumId: number) {
    const stratum = getQuota(s, stratumId);
    if (stratum && stratum.status !== QuotaStatus.Closed) {
        const completes = useOnlines ? stratum.frequency + stratum.online : stratum.frequency;
        return stratum.objective <= 0 || completes < stratum.objective;
    }
    return false;
}

function getAvailableStratum(s: ParserState, useOnlines: boolean, strata: number[]) {
    let missingCompletes = 0;
    let lastMissingCompletes = 0;
    let result = 0;
    strata.every((stratumId) => {
        if (isStratumAvailable(s, useOnlines, stratumId)) {
            const stratum = getQuota(s, stratumId);
            missingCompletes = stratum.objective <= 0 ? 0 : stratum.objective - stratum.online;

            if (useOnlines && stratum.objective > 0) missingCompletes -= stratum.online;

            if (missingCompletes > lastMissingCompletes) {
                lastMissingCompletes = missingCompletes;
                result = stratum.id;
            }
        }
        return true;
    });

    return result;
}

/**
 * Returns 1 if any of the specified strata are not closed or completed.
 * If UseOnlines = 1, online respondents count as completes.
 */
export function qtisavail(s: ParserState, useOnlines: number, ...strata: number[]) {
    const online = as.bool(s, useOnlines);
    const hasAvailableStratum = strata.some((stratum) => {
        return isStratumAvailable(s, online, stratum);
    });
    return hasAvailableStratum ? 1 : 0;
}

/**
 * Returns 1 if all the specified strata are not closed or completed.
 * If UseOnlines = 1, online respondents count as completes.
 */
export function qtareavail(s: ParserState, useOnlines: number, ...strata: number[]) {
    const online = as.bool(s, useOnlines);
    const allStrataAvailable =
        strata.length > 0 &&
        strata.every((stratum) => {
            return isStratumAvailable(s, online, stratum);
        });
    return allStrataAvailable ? 1 : 0;
}

/**
 * Returns the stratum ID in the specified list that is opened and missing the most completes.
 * If UseOnlines = 1, online respondents count as completes.
 */
export function getavailqt(s: ParserState, useOnlines: number, ...strata: number[]) {
    const online = as.bool(s, useOnlines);
    return getAvailableStratum(s, online, strata);
}

/**
 * Returns the stratum ID randomly selected among the list of specified strata opened but not completed.
 * If UseOnlines = 1, online respondents count as completes.
 */
export function selrandqt(s: ParserState, useOnlines: number, ...strata: number[]) {
    const online = as.bool(s, useOnlines);
    const availStrata = strata.filter((stratum) => isStratumAvailable(s, online, stratum));

    if (availStrata.length > 0) {
        return availStrata[Math.floor(Math.random() * availStrata.length)];
    }
    return 0;
}

function getRespondentStrata(s: ParserState, strata: number[]) {
    return strata.filter((stratumId) => {
        const stratum = getQuota(s, stratumId);
        if (stratum) {
            const result = parse(stratum.condition, s);
            return new EvaluationResult(result).asBoolean();
        }

        return false;
    });
}

/**
 * Returns 1 if any of the specified strata are not closed or completed.
 * If UseOnlines = 1, online respondents count as completes.
 */
export function qtisavailresp(s: ParserState, useOnlines: number, ...strata: number[]) {
    const respondentStrata = getRespondentStrata(s, strata);
    return qtisavail(s, useOnlines, ...respondentStrata);
}

/**
 * Returns 1 if all the specified strata are not closed or completed.
 */
export function qtareavailresp(s: ParserState, useOnlines: number, ...strata: number[]) {
    const respondentStrata = getRespondentStrata(s, strata);
    return qtareavail(s, useOnlines, ...respondentStrata);
}

/**
 * Returns the ID of the first stratum to which the respondent belongs that is opened and missing the most completes.
 */
export function getavailqtresp(s: ParserState, useOnlines: number, ...strata: number[]) {
    const respondentStrata = getRespondentStrata(s, strata);
    return getavailqt(s, useOnlines, ...respondentStrata);
}

/**
 * Returns the ID of a stratum randomly selected among the list of strata to which the respondent belongs that are opened but not completed.
 */
export function selrandqtresp(s: ParserState, useOnlines: number, ...strata: number[]) {
    const respondentStrata = getRespondentStrata(s, strata);
    return selrandqt(s, useOnlines, ...respondentStrata);
}

/**
 * Computes a value from a specific loop.
 */
export function rxy(s: ParserState, loop: any, value: any) {
    loop = as.val(s, loop);
    s = { ...s, matrix: loop };
    return as.val(s, value);
}

/**
 * Number of loops with data in at least one variable.
 */
export function nbl(s: ParserState, variable: any) {
    variable = as.val(s, variable);
    return getMentionsCount(s.interview.variables, variable.name);
}

/**
 * Get a value over the loops.
 */
export function loop(s: ParserState, val: any) {
    // Store variable names.
    const variables: string[] = [];
    as.val(
        {
            ...s,
            didParseVariable: (v) => {
                // Hook didParseVariable to get the list of variables.
                if (s.didParseVariable) s.didParseVariable(v);
                variables.push(v);
            },
        },
        val,
    );
    // If no variables were found in the expression, return.
    if (!variables.length) return val;
    // Get the max loop for the variables.
    const maxLoop = getMentionsCount(s.interview.variables, variables[0]);
    // Return the results for all loops.
    return range(1, maxLoop + 1).map((l) => as.val({ ...s, matrix: l }, val));
}

/**
 * Assigns mentions of source to first mention of destination in corresponding row.
 */
export function fsr(s: ParserState, src: any, dst: any) {
    const srcVar = as.val(s, src);
    const dstVar = as.val(s, dst);
    const srcMentions = getMentions(s.interview.variables, srcVar.name, 1);
    const maxLoopSize = getMaxLoopSize(dstVar.block);
    for (let i = 0; i < maxLoopSize; i++) {
        const currentLoop = i + 1;
        const srcMention = srcMentions.find(({ value }) => parseInt(value) === currentLoop);
        s.dispatch(
            setVariableMentions(
                dstVar.name,
                (mentions) =>
                    srcMention
                        ? mentions.set(srcMention.value, srcMention.openEnd)
                        : mentions.clear(),
                currentLoop,
            ),
        );
    }
    return 1;
}

/**
 * Selects a specified loop in a block.
 */
export function rnb(s: ParserState, loop: any, ...vn: any[]) {
    loop = as.val(s, loop);
    if (!loop) return null;
    loop = as.number(s, loop);
    forEach(vn, (v) => {
        const { name } = v.block;
        s.dispatch(setLoopRow(name, loop));
    });
    return loop;
}

/**
 * Copy a variable label to all listed variables and returns its value.
 */
export function mst(s: ParserState, v1: any, ...vn: any[]) {
    v1 = as.val(s, v1);
    if (!v1) return null;
    const values = typeof v1 === "string" ? [v1] : v1.labels();
    setVariablesValues(s, values, vn);
    return as.number(s, as.number(s, v1));
}

/**
 * Copy a variable value to all listed variables and returns its value.
 */
export function mov(s: ParserState, v1: any, ...vn: any[]) {
    v1 = as.string(s, v1);
    setVariablesValues(s, v1, vn);
    return v1;
}

/**
 * Return the number with the lowest value.
 */
export function min(s: ParserState, ...vn: any[]) {
    const f = (v: any) =>
        Math.min(...v.map(getValidNumber).map((x) => (isNaN(x) ? INVALID : x || 0)));
    return vec(s, vn, f);
}

/**
 * Maximum of arguments.
 */
export function max(s: ParserState, ...vn: any[]) {
    const f = (v: any) =>
        Math.max(...v.map(getValidNumber).map((x) => (isNaN(x) ? INVALID : x || 0)));
    return vec(s, vn, f);
}

/**
 * Sum of arguments.
 */
export function sum(s: ParserState, vn: any[]) {
    return validateOutput(vec(s, vn, (v) => v.map(getValidNumber).reduce((a, b) => a + b)));
}

/**
 * Sum with replacement of missing values. SMR(R,VAL1, VAL2, VAL3..) Sums VAL1 – VALn replacing missing values with R.
 */
export function smr(s: ParserState, v1: any, vn: any[]) {
    v1 = as.val(s, v1);
    v1 = as.number(s, v1);
    if (v1 === "" || v1 === null) return INVALID;
    const f = (v: number[]) => v.map((x) => (isInvalid(x) ? v1 : x)).reduce((a, b) => a + b);
    return validateOutput(vec(s, vn, f));
}

/**
 * Sum of square of arguments.
 */
export function sm2(s: ParserState, vn: any[]) {
    const f = (v: number[]) =>
        v
            .map(getValidNumber)
            .map((x) => x * x)
            .reduce((a, b) => a + b);
    return validateOutput(vec(s, vn, f));
}

/**
 * Returns the argument of rank P0. For example: PRJ(Q1,A,B,C) returns B (if value of Q1 is 2).
 */
export function prj(s: ParserState, vn: any[]) {
    vn = as.val(s, vn);
    if (vn.length < 2) return NaN;
    const v = vn.map((v) => as.number(s, v));
    if (v[0] <= 0) return INVALID;
    if (v.length > v[0]) {
        const value = getValidNumber(v[v[0]]);
        if (!isInvalid(value)) return value;
    }
    return INVALID;
}

/**
 * Returns the first i such that VALi equals A; returns 0 if the value A is not found in the list of VALi.
 */
export function ind(s: ParserState, vn: any[]) {
    const f = (v: any) => {
        const values = v.map((x: any) => (isNaN(x) ? null : x));
        return values.slice(1).indexOf(values[0]) + 1;
    };
    return vec(s, vn, f);
}

/**
 * A multiplication between numeric values.
 */
export function mul(s: ParserState, a: any, b: any) {
    const f = (v1: any, v2: any) => (v1 || 0) * (v2 || 0);
    return validateInput(s, f, a, b);
}

/**
 * An exponentiation is an exponentiation operation between numeric values.
 */
export function pow(s: ParserState, a: any, b: any) {
    const f = (v1: any, v2: any) => (v2 === NaN ? 1 : Math.pow(v1 || 0, v2 || 0));
    return validateInput(s, f, a, b);
}

/**
 * A division between numeric values.
 */
export function div(s: ParserState, a: any, b: any) {
    a = as.number(s, a);
    if (isEmptyOrNaN(a)) a = 0;
    b = as.number(s, b);
    if (isEmptyOrNaN(b) || b === 0) b = INVALID;

    if ([a, b].some((v) => v === INVALID || isNaN(v))) return INVALID;
    return validateOutput(a / b);
}

/**
 * An addition between numeric values.
 */
export function add(s: ParserState, a: any, b: any) {
    const f = (v1: any, v2: any) => {
        if (v1 === null) v1 = 0;
        if (v2 === null) v2 = 0;
        if ([v1, v2].some((v) => [undefined, NaN].includes(v))) return NaN;
        return v1 + v2;
    };
    return validateInput(s, f, a, b);
}

/**
 * A subtraction between numeric values.
 */
export function sub(s: ParserState, a: any, b: any) {
    const f = (v1: any, v2: any) => {
        if (v1 === null) v1 = 0;
        if (v2 === null) v2 = 0;
        if ([v1, v2].some((v) => [undefined, NaN].includes(v))) return NaN;
        return v1 - v2;
    };
    return validateInput(s, f, a, b);
}
